// "End to end" lifecycle: a five-stage stepper; the active stage shows its modules
// and where FrothAI helps.
import { useEffect, useRef, useState } from "react";
import {
  Activity,
  Bot,
  Bug,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  FileBarChart,
  FileCode2,
  FileText,
  KanbanSquare,
  ListChecks,
  ScanSearch,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import "../../css/LifecycleMap.css";
import FrothAiIcon from "../common/FrothAiIcon";

const stages = [
  {
    n: "01",
    name: "Plan",
    text: "Capture what to build and organise the work.",
    modules: [
      { icon: FileText, title: "Requirements", text: "By build and module, synced with Jira, Plane or Azure DevOps" },
      { icon: KanbanSquare, title: "Task Management", text: "Kanban board for QA work" },
    ],
    ai: "Generates requirements and tasks",
  },
  {
    n: "02",
    name: "Design",
    text: "Turn requirements into tests and scripts.",
    modules: [
      { icon: ListChecks, title: "Test Management", text: "Scenarios and test cases with review and approval" },
      { icon: Bot, title: "Test Automation", text: "TestOps Recorder, Playwright and Appium scripts" },
    ],
    ai: "Generates test cases and scripts",
  },
  {
    n: "03",
    name: "Execute",
    text: "Run manual, API and automated tests.",
    modules: [
      { icon: ClipboardCheck, title: "Test Plans & Cycles", text: "Manual and automated cycles with evidence" },
      { icon: Workflow, title: "API Testing", text: "Requests and multi-step API workflows" },
      { icon: CalendarClock, title: "Scheduler", text: "Unattended runs on your CI runners" },
    ],
    ai: "Answers questions about your project",
  },
  {
    n: "04",
    name: "Assure",
    text: "Check performance, security, code and AI quality.",
    modules: [
      { icon: Activity, title: "Performance Test Lab", text: "Locust load tests against KPIs" },
      { icon: ShieldCheck, title: "Security Test Lab", text: "OWASP ZAP scans and cyber-hygiene score" },
      { icon: FileCode2, title: "Code Analyser", text: "Semgrep SAST and Gitleaks secret scanning" },
      { icon: ScanSearch, title: "AI Evaluation", text: "LLM-judged chatbot quality" },
      { icon: Bot, title: "Automation Test Lab", text: "Playwright and Appium runs on your CI runners" },
    ],
    ai: "Writes security and code reports",
  },
  {
    n: "05",
    name: "Release",
    text: "Close defects and sign off with confidence.",
    modules: [
      { icon: Bug, title: "Defect Management", text: "Severity, priority and aging, with two-way sync" },
      { icon: FileBarChart, title: "Reports & Sign-off", text: "Test Plan Summary Report and approvals" },
    ],
    ai: "Assesses release readiness",
  },
];

const AUTO_MS = 4500; // how long each stage stays before the stepper moves on by itself

export default function LifecycleMap() {
  const [active, setActive] = useState(0);
  // 1 when moving forward, -1 when moving back; the stage change slides in from that side.
  const [dir, setDir] = useState(1);
  const [reduced] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const rootRef = useRef(null);
  const tabs = useRef([]);
  const last = stages.length - 1;

  const select = (i, d = i >= active ? 1 : -1) => {
    setDir(d);
    setActive(i);
  };

  const go = (i, d) => {
    select(i, d);
    if (tabs.current[i]) tabs.current[i].focus();
  };

  // Arrow keys move between stages, as in a standard tablist.
  const onKeyDown = e => {
    if (e.key === "ArrowRight") go(active === last ? 0 : active + 1, 1);
    else if (e.key === "ArrowLeft") go(active === 0 ? last : active - 1, -1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(last);
    else return;
    e.preventDefault();
  };

  // The automatic move only runs while the section is on screen.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), {
      rootMargin: "0px 0px -20% 0px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // A small automatic move: step to the next stage every few seconds while the
  // section is on screen and not hovered or focused. Depending on `active`
  // restarts the countdown after every change, manual clicks included.
  // While it runs, the active step shows a countdown ring and the Next button a filling bar.
  const running = visible && !hovered && !reduced;
  useEffect(() => {
    if (!running) return undefined;
    const timer = setTimeout(() => {
      setDir(1);
      setActive(a => (a + 1) % stages.length);
    }, AUTO_MS);
    return () => clearTimeout(timer);
  }, [running, active]);

  return (
    <div
      ref={rootRef}
      className={`lc${running ? " is-auto" : ""}`}
      style={{ "--progress": active / last, "--dir": dir, "--lc-auto": `${AUTO_MS}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <div className="lc__steps" role="tablist" aria-label="QA lifecycle stages" onKeyDown={onKeyDown}>
        <span className="lc__track" aria-hidden="true">
          <i />
          <b />
        </span>
        {stages.map((s, i) => (
          <button
            key={s.n}
            ref={el => (tabs.current[i] = el)}
            type="button"
            role="tab"
            id={`lc-tab-${i}`}
            aria-selected={i === active}
            aria-controls={`lc-panel-${i}`}
            tabIndex={i === active ? 0 : -1}
            className={`lc-step${i === active ? " is-active" : ""}${i < active ? " is-done" : ""}`}
            onClick={() => select(i)}
          >
            <span className="lc-step__num">{s.n}</span>
            <span className="lc-step__name">{s.name}</span>
            <span className="lc-step__count">
              {s.modules.length} module{s.modules.length > 1 ? "s" : ""}
            </span>
          </button>
        ))}
      </div>

      {stages.map((s, i) => (
        <div
          key={s.n}
          role="tabpanel"
          id={`lc-panel-${i}`}
          aria-labelledby={`lc-tab-${i}`}
          className="lc-panel"
          hidden={i !== active}
        >
          <div className="lc-panel__intro">
            <span className="lc-panel__ghost" aria-hidden="true">
              {s.n}
            </span>
            <span className="lc-panel__kicker">
              Stage {i + 1} of {stages.length}
            </span>
            <h3 className="lc-panel__name">{s.name}</h3>
            <p className="lc-panel__text">{s.text}</p>
            <p className="lc-panel__ai">
              <span className="lc-panel__ai-icon">
                <FrothAiIcon size={16} />
              </span>
              <span>
                <strong>FrothAI</strong>
                {s.ai}
              </span>
            </p>
          </div>
          <ul className="lc-panel__mods">
            {s.modules.map(({ icon: Icon, title, text }, m) => (
              <li className="lc-mod" key={title} style={{ "--i": m }}>
                <span className="lc-mod__icon">
                  <Icon size={20} aria-hidden="true" />
                </span>
                <strong>{title}</strong>
                <small>{text}</small>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="lc__foot">
        <span className="lc__band">
          <FrothAiIcon size={16} />
          Built into every stage, so one platform covers the lifecycle from plan to release.
        </span>
        <span className="lc__nav">
          <button type="button" onClick={() => select(active - 1, -1)} disabled={active === 0} aria-label="Previous stage">
            <ChevronLeft size={18} />
          </button>
          <button
            key={active}
            type="button"
            className="lc__next"
            onClick={() => select(active + 1, 1)}
            disabled={active === last}
          >
            {active === last ? "Next" : `Next: ${stages[active + 1].name}`}
            <ChevronRight size={18} />
          </button>
        </span>
      </div>
    </div>
  );
}
